'use client';

import { useCallback, useEffect, useState } from 'react';
import BodyClass from '@plone/volto/helpers/BodyClass/BodyClass';
import { FocusTrap } from 'focus-trap-react';
import { createPortal } from 'react-dom';
import { defineMessages, injectIntl } from 'react-intl';
import { useHistory } from 'react-router';
import SpeechRecognition, {
  useSpeechRecognition,
} from 'react-speech-recognition';

import { withClientCheck } from './withClientCheck';

export const messages = defineMessages({
  listening: {
    id: 'VoiceSearch.Listening',
    defaultMessage: "I'm listening...",
  },
  disabled: {
    id: 'VoiceSearch.Disabled',
    defaultMessage: 'Check microphone permissions.',
  },
  repeat: {
    id: 'VoiceSearch.Repeat',
    defaultMessage: 'Click the button to redo the search.',
  },
  success: {
    id: 'VoiceSearch.Success',
    defaultMessage: 'Search completed successfully.',
  },
  listeningTitle: {
    id: 'VoiceSearch.ListeningTitle',
    defaultMessage: 'Listening...',
  },
  disabledTitle: {
    id: 'VoiceSearch.DisabledTitle',
    defaultMessage: 'Microphone disabled.',
  },
  repeatTitle: {
    id: 'VoiceSearch.RepeatTitle',
    defaultMessage: 'Did not understand.',
  },
  stopVoiceSearch: {
    id: 'VoiceSearch.StopVoiceSearch',
    defaultMessage: 'Stop voice search',
  },
  redoVoiceSearch: {
    id: 'VoiceSearch.RedoVoiceSearch',
    defaultMessage: 'Redo voice search',
  },
  close: {
    id: 'VoiceSearch.Close',
    defaultMessage: 'Close',
  },
  stop: {
    id: 'VoiceSearch.Stop',
    defaultMessage: 'Stop',
  },
  redoSearch: {
    id: 'VoiceSearch.RedoSearch',
    defaultMessage: 'Redo search',
  },
  startVoiceSearch: {
    id: 'VoiceSearch.StartVoiceSearch',
    defaultMessage: 'Start voice search',
  },
});

const VoiceSearch = ({ intl }) => {
  const history = useHistory();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [speechRecognitionSupported, setSpeechRecognitionSupported] =
    useState(null); // Adicionado
  const {
    listening,
    resetTranscript,
    transcript,
    finalTranscript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable,
  } = useSpeechRecognition();

  useEffect(() => {
    setSpeechRecognitionSupported(browserSupportsSpeechRecognition);
  }, [browserSupportsSpeechRecognition]);

  const playAudio = useCallback((src) => {
    const audio = new Audio(src);
    audio.play();
  }, []);

  const noSupportRecognition =
    speechRecognitionSupported === false ||
    !window.webkitSpeechRecognition ||
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (noSupportRecognition) return;

    const recognition = SpeechRecognition.getRecognition();

    const handleEndListening = () =>
      playAudio(
        'https://www.gov.br/++resource++govbr.theme/audio/record-end.mp3',
      );
    const handleStartListening = () =>
      playAudio(
        'https://www.gov.br/++resource++govbr.theme/audio/record-start.mp3',
      );

    recognition.addEventListener('end', handleEndListening);
    recognition.addEventListener('start', handleStartListening);
    return () => {
      recognition.removeEventListener('end', handleEndListening);
      recognition.removeEventListener('start', handleStartListening);
    };
  }, [playAudio, noSupportRecognition]);

  useEffect(() => {
    if (finalTranscript) {
      setIsModalVisible(false);
      SpeechRecognition.stopListening();
      history.push(`/search?origem=voz&SearchableText=${finalTranscript}`);
    }
  }, [finalTranscript, history]);

  const startListening = () => {
    if (!isMicrophoneAvailable) return;

    resetTranscript();
    SpeechRecognition.startListening({
      continuous: false,
      language: 'pt-br',
      interimResults: true,
    });
  };

  const openModal = () => {
    setIsModalVisible(true);
    startListening();
  };

  const closeModal = () => {
    setIsModalVisible(false);
    SpeechRecognition.stopListening();
  };

  const getCurrentState = () => {
    if (!isMicrophoneAvailable) return 'disabled';
    return listening
      ? 'listening'
      : finalTranscript === ''
        ? 'repeat'
        : 'success';
  };

  const legendTexts = {
    listening: intl.formatMessage(messages.listening),
    disabled: intl.formatMessage(messages.disabled),
    repeat: intl.formatMessage(messages.repeat),
    success: intl.formatMessage(messages.success),
  };

  const titleTexts = {
    listening: transcript || intl.formatMessage(messages.listeningTitle),
    disabled: intl.formatMessage(messages.disabledTitle),
    repeat: intl.formatMessage(messages.repeatTitle),
    success: finalTranscript,
  };

  const ListeningButton = () => (
    <button
      className="br-button primary circle voice listening"
      type="button"
      aria-label={intl.formatMessage(messages.stopVoiceSearch)}
      title={intl.formatMessage(messages.stopVoiceSearch)}
      onClick={closeModal}
    >
      <i className="fas fa-microphone stop-voice" aria-hidden="true" />
    </button>
  );

  const RepeatButton = () => (
    <button
      className="br-button circle voice repeat"
      type="button"
      aria-label={intl.formatMessage(messages.redoVoiceSearch)}
      title={intl.formatMessage(messages.redoVoiceSearch)}
      onClick={startListening}
    >
      <i className="fas fa-microphone" aria-hidden="true" />
    </button>
  );

  const ModalContent = () => (
    <>
      <BodyClass className="noScroll" />
      <div className="overlay" />
      <FocusTrap>
        <div className="modal" tabIndex="-1">
          <button
            className="br-button small circle btnFechar"
            title={intl.formatMessage(messages.close)}
            onClick={closeModal}
            aria-label={intl.formatMessage(messages.close)}
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>

          <div className="header">
            <h2>{titleTexts[getCurrentState()]}</h2>
            {listening ? <ListeningButton /> : <RepeatButton />}
          </div>

          <div className="content">
            <p>{legendTexts[getCurrentState()]}</p>
          </div>

          <div className="br-divider divider" />

          <div className="footer">
            {listening || !isMicrophoneAvailable ? (
              <button
                className="br-button small secondary"
                aria-label={intl.formatMessage(messages.stopVoiceSearch)}
                title={intl.formatMessage(messages.stopVoiceSearch)}
                onClick={closeModal}
              >
                {intl.formatMessage(messages.stop)}
              </button>
            ) : (
              <button
                className="br-button small secondary"
                aria-label={intl.formatMessage(messages.redoVoiceSearch)}
                title={intl.formatMessage(messages.redoVoiceSearch)}
                onClick={startListening}
              >
                {intl.formatMessage(messages.redoSearch)}
              </button>
            )}
          </div>
        </div>
      </FocusTrap>
    </>
  );

  if (speechRecognitionSupported === null) return null; // Evita renderizar até que o suporte seja verificado

  if (noSupportRecognition) return null;

  return (
    <>
      {isModalVisible &&
        createPortal(<ModalContent />, document.getElementById('search-modal'))}
      <button
        className="br-button circle voice"
        type="button"
        aria-label={intl.formatMessage(messages.startVoiceSearch)}
        title={intl.formatMessage(messages.startVoiceSearch)}
        onClick={openModal}
      >
        <i className="fas fa-microphone" aria-hidden="true" />
      </button>
    </>
  );
};

export default withClientCheck(injectIntl(VoiceSearch));
